import { useState } from 'react';
import { App, Button, Card, Input, Modal, Popconfirm, Select, Space, Table, Typography, type TableColumnsType } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { errMsg } from '../../lib/errors';
import { formatRupees } from '../../lib/money';
import { QueryError } from '../../lib/QueryState';
import { LoadMore } from '../../lib/useCursorPages';
import { useAgencyPayouts } from '../agencies/agencyLists';
import { payoutColumns } from '../agencies/agencyViews';
import type { AgencyPayoutRow } from '../agencies/agencyApi';

const FILTERS = [
  { value: 'pending_approval,approved', label: 'To do (awaiting approval / to pay)' },
  { value: 'settled', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

/**
 * Agency payouts (finance): approve → transfer out-of-band → mark paid with the
 * UTR / reference, or reject (the amount returns to the agency's balance).
 */
export function AgencyPayoutsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [status, setStatus] = useState('pending_approval,approved');
  const [paying, setPaying] = useState<AgencyPayoutRow | null>(null);
  const [payRef, setPayRef] = useState('');
  const [rejecting, setRejecting] = useState<AgencyPayoutRow | null>(null);
  const [reason, setReason] = useState('');
  const list = useAgencyPayouts('/admin/agency-payouts', { status: status || undefined });

  const done = (msg: string) => {
    message.success(msg);
    void qc.invalidateQueries({ queryKey: ['admin', 'agency-lists'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'agencies'] });
  };
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/admin/agency-payouts/${id}/approve`),
    onSuccess: () => done('Approved — transfer the money, then mark it paid'),
    onError: (e) => message.error(errMsg(e, 'Could not approve')),
  });
  const markPaid = useMutation({
    mutationFn: ({ id, ref }: { id: string; ref: string }) =>
      api.post(`/admin/agency-payouts/${id}/mark-paid`, { paymentReference: ref || undefined }),
    onSuccess: () => {
      done('Marked as paid');
      setPaying(null);
      setPayRef('');
    },
    onError: (e) => message.error(errMsg(e, 'Could not mark paid')),
  });
  const reject = useMutation({
    mutationFn: ({ id, reason: r }: { id: string; reason: string }) => api.post(`/admin/agency-payouts/${id}/reject`, { reason: r }),
    onSuccess: () => {
      done('Rejected — the amount is back in the agency balance');
      setRejecting(null);
      setReason('');
    },
    onError: (e) => message.error(errMsg(e, 'Could not reject')),
  });

  const actions: TableColumnsType<AgencyPayoutRow>[number] = {
    title: '',
    key: 'actions',
    render: (_, r) => (
      <Space wrap>
        {r.status === 'pending_approval' && (
          <Can permission="agency_payouts.approve">
            <Popconfirm title={`Approve ${formatRupees(r.netPayoutPaise)}?`} onConfirm={() => approve.mutate(r.id)}>
              <Button size="small" type="primary">
                Approve
              </Button>
            </Popconfirm>
          </Can>
        )}
        {r.status === 'approved' && (
          <Can permission="agency_payouts.mark_paid">
            <Button size="small" type="primary" onClick={() => setPaying(r)}>
              Mark paid
            </Button>
          </Can>
        )}
        {(r.status === 'pending_approval' || r.status === 'approved') && (
          <Can permission="agency_payouts.reject">
            <Button size="small" danger onClick={() => setRejecting(r)}>
              Reject
            </Button>
          </Can>
        )}
      </Space>
    ),
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        Agency payouts
      </Typography.Title>
      <Card>
        <Select style={{ width: 300, marginBottom: 12 }} value={status} onChange={setStatus} options={FILTERS} />
        {list.isError ? (
          <QueryError error={list.error} what="agency payouts" onRetry={list.refetch} />
        ) : (
          <>
            <Table
              rowKey="id"
              size="small"
              loading={list.isLoading}
              columns={payoutColumns({ showAgency: true, extra: actions })}
              dataSource={list.rows}
              pagination={false}
              scroll={{ x: 1100 }}
            />
            <LoadMore list={list} noun="payouts" />
          </>
        )}
      </Card>

      <Modal
        open={!!paying}
        title={paying ? `Mark ${formatRupees(paying.netPayoutPaise)} paid to ${paying.agency?.name ?? 'agency'}` : ''}
        onCancel={() => setPaying(null)}
        okText="Mark paid"
        confirmLoading={markPaid.isPending}
        onOk={() => paying && markPaid.mutate({ id: paying.id, ref: payRef })}
      >
        <Typography.Paragraph type="secondary">Enter the UTR / transaction reference of the transfer you made.</Typography.Paragraph>
        <Input placeholder="UTR / reference" value={payRef} onChange={(e) => setPayRef(e.target.value)} />
      </Modal>

      <Modal
        open={!!rejecting}
        title="Reject payout"
        onCancel={() => setRejecting(null)}
        okText="Reject"
        okButtonProps={{ danger: true, disabled: reason.trim().length < 10 }}
        confirmLoading={reject.isPending}
        onOk={() => rejecting && reject.mutate({ id: rejecting.id, reason: reason.trim() })}
      >
        <Typography.Paragraph type="secondary">The amount goes back to the agency&apos;s payable balance.</Typography.Paragraph>
        <Input.TextArea rows={3} placeholder="Reason (at least 10 characters)" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </Space>
  );
}
